import React, { useEffect, useRef } from 'react';
import {
  BodyText,
  BodyTextStyle,
  Button,
  CheckCircleIcon,
  Stack,
  Title,
  mq
} from '@kvdbil/components';
import styled from 'styled-components';
import {
  LeasingPartnerPresentationFragment,
  LeasingPromotionCardFragment
} from '~/config/generated/graphql';
import { useTranslation } from '~/Locale';
import { LeasingInterestFormValues } from './LeasingInterestForm';
import LeasingPromotionSummary from './LeasingPromotionSummary';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
`;

const Icon = styled(CheckCircleIcon)`
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
  color: ${({ theme }) => theme.colors.secondary.mainAlt};
`;

const Heading = styled(Title)`
  && {
    margin: 0;
  }
`;

const Details = styled.dl`
  margin: 0;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 1rem;
  ${BodyTextStyle};
`;

const DetailLabel = styled.dt`
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
`;

const DetailValue = styled.dd`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;

  ${mq('tablet')} {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

type Props = {
  selectedCard: LeasingPromotionCardFragment;
  priceFootnote?: string;
  submittedValues: Pick<
    LeasingInterestFormValues,
    'fullName' | 'email' | 'phoneNumber'
  >;
  onClose: () => void;
  title: LeasingPartnerPresentationFragment['interestConfirmationTitle'];
  text: LeasingPartnerPresentationFragment['interestConfirmationText'];
  subjectHeading: LeasingPartnerPresentationFragment['interestConfirmationSubjectHeading'];
  detailsHeading: LeasingPartnerPresentationFragment['interestConfirmationDetailsHeading'];
};

const LeasingInterestConfirmation = ({
  selectedCard,
  priceFootnote,
  submittedValues,
  onClose,
  title,
  text,
  subjectHeading,
  detailsHeading
}: Props) => {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const details = [
    { label: t('Name'), value: submittedValues.fullName },
    { label: t('Email'), value: submittedValues.email },
    { label: t('Phone number'), value: submittedValues.phoneNumber }
  ];

  return (
    <Stack rowGap="1rem">
      <Header>
        <Icon aria-hidden="true" />
        <Heading as="h2" id="modal-title">
          {title}
        </Heading>
      </Header>

      {text && <BodyText>{text}</BodyText>}

      {subjectHeading && <BodyText>{subjectHeading}</BodyText>}

      <LeasingPromotionSummary
        {...selectedCard}
        priceFootnote={priceFootnote}
      />

      {detailsHeading && <BodyText>{detailsHeading}</BodyText>}

      <Details>
        {details.map(({ label, value }) => (
          <DetailRow key={label}>
            <DetailLabel>{label}:</DetailLabel>
            <DetailValue>{value}</DetailValue>
          </DetailRow>
        ))}
      </Details>

      <ButtonRow>
        <Button
          ref={closeButtonRef}
          type="button"
          color="secondary"
          variant="flat"
          onClick={onClose}
        >
          {t('Close')}
        </Button>
      </ButtonRow>
    </Stack>
  );
};

export default LeasingInterestConfirmation;
